import { useQuery } from "@apollo/client";
import { withJsonFormsMultiEnumProps } from "@jsonforms/react";
import { Dropdown } from "pepsico-ds";
import { useEffect, useState } from "react";
import { getContentList } from "../../../graphql/queries/contentQueries";

const ACTIVE_CONTENT_STATUS = "ACTIVE";

const ContentSelectComponent = ({
  handleChange,
  label,
  path,
  required,
  visible,
  enabled,
  data,
  uischema,
}) => {
  const contentDefinitionSlug = uischema.options?.contentDefinitionSlug || "brand";
  const selectionType = uischema.selection || "multiple";
  const isSingleSelect = selectionType === "single";

  const [contentOptions, setContentOptions] = useState([]);
  const [selected, setSelected] = useState(isSingleSelect ? [] : []);
  const { data: contentData } = useQuery(getContentList, {
    variables: {
      filter: {
        contentDefinitionSlug,
        status: [ACTIVE_CONTENT_STATUS],
      },
    },
  });

  const formatOptions = (options) =>
    options.map((option) => ({
      id: option.id,
      displayText:
        option.contentData?.activity_type ||
        option.title ||
        option.contentData?.title,
    }));

  // Initialize or update selected values when content data or form data changes
  useEffect(() => {
    if (!contentData?.listContent) return;

    const formattedOptions = formatOptions(contentData.listContent);
    setContentOptions(contentData.listContent);

    if (isSingleSelect) {
      // For single select, data will be a single ID
      if (data) {
        const selectedItem = formattedOptions.find((item) => item.id === data);
        if (selectedItem) {
          setSelected([selectedItem]); // Dropdown expects an array with one item for single select
        } else {
          setSelected([]);
        }
      } else {
        setSelected([]);
      }
    } else {
      // For multi-select, data will be an array of IDs
      if (data?.length > 0) {
        const selectedItems = data
          ?.map((id) => formattedOptions.find((item) => item.id === id))
          .filter(Boolean);
        setSelected(selectedItems);
      } else {
        setSelected([]);
      }
    }
  }, [contentData, data, isSingleSelect]);

  const handleSelectValue = (val) => {
    if (isSingleSelect) {
      // For single select, the Dropdown returns an array with one item
      setSelected(val || []);
      // Pass the ID directly for single select
      handleChange(path, val && val.length > 0 ? val[0].id : null);
    } else {
      // For multi-select
      setSelected(val || []);
      // Pass an array of IDs for multi-select
      handleChange(path, val ? val.map((item) => item.id) : []);
    }
  };

  // Create a custom placeholder that shows selected values when disabled
  let customPlaceholder = uischema.placeholder || "Select";
  if (selected && selected.length > 0) {
    customPlaceholder = selected.map((item) => item.displayText).join(", ");
  }

  return visible ? (
    <div className={`multiple-select-wrap peacock-dropdown-show-label`}>
      <Dropdown
        childList={formatOptions(contentOptions)}
        selection={selectionType}
        isRequired={required}
        label={label}
        disabled={!enabled}
        selectedValue={selected}
        placeholder={customPlaceholder}
        setSelectedValue={handleSelectValue}
      />
    </div>
  ) : null;
};

export const ContentSelectControl = withJsonFormsMultiEnumProps(
  ContentSelectComponent
);

export { ContentSelectComponent };
